/**
 * Presentación del **Dashboard de Autoservicio**.
 *
 * Concentra las etiquetas, colores y mensajes de error para que las cuatro
 * pestañas (resumen, por turno, detalle y cola) hablen el mismo idioma. Los
 * textos se ramifican por el **código** de la API, nunca por el texto del
 * mensaje: el servicio puede reescribir su `message` sin que la pantalla deje de
 * explicar qué hacer.
 */

import type { BadgeVariant } from '@/components/ui/badge';
import { ApiError } from '@/lib/http/api-error';

import type { OutboxFila, TurnoAutoservicio } from './autoservicio.types';

/** Mensaje legible de un error (sin exponer detalles técnicos). */
export function mensajeDe(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;

  return porDefecto;
}

/** Ayuda por código de la API. */
const AYUDA_POR_CODIGO: Record<string, string> = {
  M2031: 'Revisa el rango de fechas (desde no posterior a hasta) y que el turno sea un número entero positivo.',
  SIN_CONEXION: 'No hay conexión con el servicio de autoservicio. Revisa que esté disponible y vuelve a intentarlo.',
  POS_CODE_INVALIDO: 'El servicio rechazó el POS Code configurado en la API. Avisa al administrador.',
  SERVICIO_NO_CONFIGURADO: 'El servicio de autoservicio no está configurado en la API (EDSUITE_POS_URL / EDSUITE_POS_CODE).',
  NO_DISPONIBLE: 'La versión desplegada del servicio todavía no tiene habilitado el dashboard de autoservicio.',
  RESPUESTA_INVALIDA: 'El servicio respondió sin los datos esperados. Vuelve a intentarlo.',
};

/**
 * Mensaje para pintar en pantalla.
 *
 * Usa la ayuda del código cuando existe y, si no, el mensaje de la API (que ya
 * viene traducido por `edsuite-api`).
 */
export function mensajeAutoservicio(error: unknown): string {
  if (error instanceof ApiError) {
    const ayuda = AYUDA_POR_CODIGO[error.code];

    if (ayuda) return ayuda;
  }

  return mensajeDe(error, 'No se pudieron cargar los datos del dashboard de autoservicio.');
}

/* ------------------------------------------------------------------ */
/* Estados de la solicitud de abastecimiento                           */
/* ------------------------------------------------------------------ */

/** Orden y etiqueta de los estados de `solicitudes.porEstado`. */
export const ESTADOS_SOLICITUD = [
  { clave: 'pending', etiqueta: 'Pendientes' },
  { clave: 'processing', etiqueta: 'En proceso' },
  { clave: 'completed', etiqueta: 'Completadas' },
  { clave: 'cancelled', etiqueta: 'Canceladas' },
  { clave: 'error', etiqueta: 'Con error' },
] as const;

export type EstadoSolicitud = (typeof ESTADOS_SOLICITUD)[number]['clave'];

export function etiquetaSolicitud(estado: string | null | undefined): string {
  const clave = String(estado ?? '').toLowerCase();
  const encontrado = ESTADOS_SOLICITUD.find((item) => item.clave === clave);

  return encontrado?.etiqueta ?? (estado || 'Sin estado');
}

export function varianteSolicitud(estado: string | null | undefined): BadgeVariant {
  switch (String(estado ?? '').toLowerCase()) {
    case 'completed':
      return 'success';
    case 'pending':
      return 'warning';
    case 'processing':
      return 'brand';
    case 'error':
      return 'danger';
    case 'cancelled':
      return 'neutral';
    default:
      return 'outline';
  }
}

/* ------------------------------------------------------------------ */
/* Estados de la documentación fiscal                                  */
/* ------------------------------------------------------------------ */

/**
 * Orden, etiqueta y color de `documentacion.porEstado`.
 *
 * `PENDIENTE_DOCUMENTO` **no** es backlog por documentar: también queda así al
 * cancelar por expiración. `SIN_DESPACHO` = despacho real 0 (no se emite
 * comprobante).
 */
export const ESTADOS_DOCUMENTACION = [
  { clave: 'PENDIENTE_DOCUMENTO', etiqueta: 'Pendiente de documento', variante: 'warning' },
  { clave: 'DOCUMENTADO', etiqueta: 'Documentado', variante: 'success' },
  { clave: 'ERROR', etiqueta: 'Error', variante: 'danger' },
  { clave: 'SIN_DESPACHO', etiqueta: 'Sin despacho', variante: 'neutral' },
] as const;

export function etiquetaDocumento(estado: string | null | undefined): string {
  const clave = String(estado ?? '').toUpperCase();
  const encontrado = ESTADOS_DOCUMENTACION.find((item) => item.clave === clave);

  return encontrado?.etiqueta ?? (clave || 'Sin estado');
}

export function varianteDocumento(estado: string | null | undefined): BadgeVariant {
  const clave = String(estado ?? '').toUpperCase();
  const encontrado = ESTADOS_DOCUMENTACION.find((item) => item.clave === clave);

  return encontrado?.variante ?? 'outline';
}

/* ------------------------------------------------------------------ */
/* Estados de la cola de reintentos                                    */
/* ------------------------------------------------------------------ */

/**
 * `completed` y `skipped` **no** son errores: son envíos resueltos.
 */
export const ESTADOS_OUTBOX = [
  { clave: 'pending', etiqueta: 'Pendientes', variante: 'warning' },
  { clave: 'processing', etiqueta: 'En proceso', variante: 'brand' },
  { clave: 'completed', etiqueta: 'Completados', variante: 'success' },
  { clave: 'failed', etiqueta: 'Fallidos', variante: 'danger' },
  { clave: 'skipped', etiqueta: 'Omitidos', variante: 'outline' },
] as const;

export function varianteOutbox(estado: string): BadgeVariant {
  const encontrado = ESTADOS_OUTBOX.find((item) => item.clave === estado);

  return encontrado?.variante ?? 'outline';
}

/* ------------------------------------------------------------------ */
/* Turnos y comprobantes                                               */
/* ------------------------------------------------------------------ */

/**
 * `turno.estado` es un entero crudo del sistema heredado (1 = vigente).
 * El estado *operativo* del turno se deduce de `hora_salida`.
 */
export function etiquetaTurnoEstado(valor: number | null | undefined): string {
  switch (Number(valor)) {
    case 1:
      return 'Vigente';
    case 2:
      return 'Anulado';
    default:
      return Number.isFinite(Number(valor)) ? `Estado ${Number(valor)}` : 'Sin estado';
  }
}

export function varianteTurnoEstado(valor: number | null | undefined): BadgeVariant {
  switch (Number(valor)) {
    case 1:
      return 'success';
    case 2:
      return 'danger';
    default:
      return 'outline';
  }
}

/** ¿El turno sigue abierto? (`hora_salida` nula) */
export function turnoEnCurso(turno: Pick<TurnoAutoservicio, 'hora_salida'>): boolean {
  return turno.hora_salida === null || turno.hora_salida === undefined || turno.hora_salida === '';
}

/** Etiqueta del comprobante emitido. */
export function etiquetaComprobante(tipo: string | null | undefined): string {
  const clave = String(tipo ?? '').toUpperCase();

  if (clave === 'BOLETA') return 'Boleta';
  if (clave === 'FACTURA') return 'Factura';
  if (clave === '') return '—';

  return tipo as string;
}

/** Número de comprobante (`BOLETA 1024`) o guion si aún no se emitió. */
export function numeroDeComprobante(
  tipo: string | null | undefined,
  numero: number | null | undefined,
): string {
  if (numero === null || numero === undefined) return '—';

  const etiqueta = etiquetaComprobante(tipo);

  return etiqueta === '—' ? String(numero) : `${etiqueta} ${numero}`;
}

/** Reintentos de una fila de la cola. */
export function reintentosDe(fila: OutboxFila): number {
  return Number(fila.reintentosContador ?? 0);
}
